import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'
import { Truck, Eye, EyeSlash, CircleNotch, ShieldWarning, CheckCircle } from '@phosphor-icons/react'

export default function EmergencyRecoveryPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setAuthData } = useAuth()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const [step, setStep] = useState<'verifying' | 'password' | 'success' | 'error'>('verifying')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tempTokens, setTempTokens] = useState<{ access_token: string; refresh_token: string } | null>(null)

  // Verify emergency token on mount
  useEffect(() => {
    async function verifyToken() {
      if (!token || !email) {
        setStep('error')
        setError('Link de emergência inválido.')
        return
      }

      try {
        const response = await api.post('/auth/emergency-recovery', { token, email })

        // Store temporary tokens (short-lived, for password change only)
        setTempTokens({
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token
        })

        setStep('password')
      } catch (err: any) {
        setStep('error')
        setError(err.response?.data?.error || 'Link de emergência inválido ou expirado.')
      }
    }

    verifyToken()
  }, [token, email])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem')
      return
    }

    if (!tempTokens) {
      setError('Sessão expirada. Tente novamente.')
      return
    }

    setLoading(true)

    try {
      // Set temporary token for the request
      localStorage.setItem('access_token', tempTokens.access_token)

      const response = await api.post('/auth/force-password-change', { newPassword })

      // Set the new full tokens
      localStorage.setItem('access_token', response.data.access_token)
      localStorage.setItem('refresh_token', response.data.refresh_token)

      // Update auth context
      setAuthData(response.data.user)

      setStep('success')

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        const redirectTo = response.data.user?.current_company?.type === 'freight-forwarder'
          ? '/freight-forwarder/dashboard'
          : '/shipper/dashboard'
        navigate(redirectTo)
      }, 2000)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao alterar senha. Tente novamente.')
      // Clear temp tokens on error
      localStorage.removeItem('access_token')
    } finally {
      setLoading(false)
    }
  }

  // Verifying state
  if (step === 'verifying') {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
            <Truck size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Eximtech</h1>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4 text-center">
          <CircleNotch size={48} className="text-green-500 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-semibold text-white mb-2">Verificando acesso...</h2>
          <p className="text-slate-400">Aguarde enquanto validamos seu link de emergência.</p>
        </div>
      </div>
    )
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
            <Truck size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Eximtech</h1>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4 text-center">
          <ShieldWarning size={48} weight="bold" className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-4">Link inválido</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg"
            style={{ backgroundColor: 'rgb(5, 150, 105)' }}
          >
            Voltar para o login
          </button>
        </div>
      </div>
    )
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
            <Truck size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Eximtech</h1>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4 text-center">
          <CheckCircle size={48} weight="bold" className="text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Conta recuperada!</h2>
          <p className="text-slate-400 mb-2">Sua senha foi alterada com sucesso.</p>
          <p className="text-slate-500 text-sm">Todas as outras sessões foram encerradas.</p>
          <p className="text-slate-400 mt-4">Redirecionando...</p>
        </div>
      </div>
    )
  }

  // Password form
  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
      <div className="flex items-center gap-3 mb-8">
        <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
          <Truck size={32} weight="bold" className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white">Eximtech</h1>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-6">
          <ShieldWarning size={32} weight="bold" className="text-yellow-500" />
          <div>
            <h2 className="text-xl font-semibold text-white">Recuperar conta</h2>
            <p className="text-slate-400 text-sm">Defina uma nova senha para sua conta</p>
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
          <p className="text-yellow-200 text-sm">
            Alguém pode ter acessado sua conta. Por segurança, todas as outras sessões serão encerradas ao definir sua nova senha.
          </p>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-slate-300 mb-2">
              Nova senha
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2.5 text-sm rounded-lg bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 transition-all pr-10"
                style={{ '--tw-ring-color': 'rgb(5, 150, 105)' } as any}
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
              Confirmar nova senha
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2.5 text-sm rounded-lg bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 transition-all"
              style={{ '--tw-ring-color': 'rgb(5, 150, 105)' } as any}
              placeholder="Digite a senha novamente"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: 'rgb(5, 150, 105)' }}
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                Recuperando conta...
              </>
            ) : (
              'Recuperar minha conta'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
